import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArrowLeft, ArrowRight, Clock, Calendar, User, HelpCircle, BookOpen, ChevronRight } from 'lucide-react';
import { ptArticles } from '@/mocks/articles/pt';
import { enArticles } from '@/mocks/articles/en';
import { deArticles } from '@/mocks/articles/de';
import { articleSlugs } from '@/mocks/articleList';

const homePaths: Record<string, string> = {
  pt: '/',
  en: '/en/',
  de: '/de/',
};

const articlePaths: Record<string, string> = {
  pt: '/artigo/',
  en: '/article/',
  de: '/artikel/',
};

interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

interface ArticleData {
  slug: string;
  title: string;
  metaDescription: string;
  readingTime: string;
  published: string;
  intro: string;
  sections: ArticleSection[];
  faq: { q: string; a: string }[];
  conclusion: string;
}

const articlesByLang: Record<string, Record<string, ArticleData>> = {
  pt: ptArticles as Record<string, ArticleData>,
  en: enArticles as Record<string, ArticleData>,
  de: deArticles as Record<string, ArticleData>,
};

export default function Article() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const currentLang = i18n.language || 'pt';

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/article/')) {
      i18n.changeLanguage('en');
    } else if (path.startsWith('/artikel/')) {
      i18n.changeLanguage('de');
    } else if (path.startsWith('/artigo/')) {
      i18n.changeLanguage('pt');
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, location.pathname]);

  const articles = articlesByLang[currentLang] || ptArticles;
  const activeSlug = slug || articleSlugs[0];
  const article = articles[activeSlug] || articles[articleSlugs[0]];
  const relatedSlugs = articleSlugs.filter((s) => s !== activeSlug).slice(0, 3);

  const goToArticle = (s: string) => {
    navigate(`${articlePaths[currentLang]}${s}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-warm-50">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        {/* Article Hero */}
        <section className="relative bg-white border-b border-warm-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <a
              href={homePaths[currentLang] || '/'}
              onClick={(e) => { e.preventDefault(); navigate(homePaths[currentLang] || '/'); }}
              className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:text-brand-800 font-medium mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {t('backToHome')}
            </a>

            <div className="flex items-center gap-3 text-xs text-warm-500 mb-4 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {article.published}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.readingTime}</span>
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {t('author')}</span>
            </div>

            <h1 className="font-display text-2xl md:text-4xl lg:text-5xl font-bold text-warm-900 leading-tight mb-6">
              {article.title}
            </h1>

            <p className="text-warm-600 text-base md:text-lg leading-relaxed max-w-3xl">
              {article.metaDescription}
            </p>
          </div>
        </section>

        {/* Article Body */}
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-none">
            <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-warm-200 mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimal%20workspace%20with%20laptop%20showing%20a%20product%20spreadsheet%2C%20clean%20white%20desk%2C%20coffee%20cup%2C%20soft%20natural%20light%20from%20a%20window%2C%20warm%20neutral%20tones%2C%20editorial%20photography%2C%20productive%20calm%20aesthetic&width=1200&height=675&seq=article-body-01-v2&orientation=landscape"
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <p className="text-warm-700 leading-relaxed text-base md:text-lg mb-8">{article.intro}</p>

            {article.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900 mt-10 mb-4">{section.heading}</h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className="text-warm-600 leading-relaxed mb-8">{para}</p>
                ))}
              </div>
            ))}

            <div className="rounded-xl bg-brand-50 border border-brand-200 p-6 md:p-8 mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-brand-700" />
                </div>
                <h3 className="font-display text-lg font-bold text-brand-800">{t('articleH2Conclusion')}</h3>
              </div>
              <p className="text-brand-700 leading-relaxed">{article.conclusion}</p>
            </div>

            {/* FAQ */}
            <div className="mt-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-brand-600" />
                </div>
                <h2 className="font-display text-xl md:text-2xl font-bold text-warm-900">{t('articleH2Faq')}</h2>
              </div>
              <div className="space-y-4">
                {article.faq.map((item, idx) => (
                  <div key={idx} className="rounded-xl border border-warm-200 bg-white p-5">
                    <h4 className="font-medium text-warm-800 mb-2">{item.q}</h4>
                    <p className="text-sm text-warm-500 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Link to Home */}
            <div className="mt-12 p-6 rounded-xl border border-brand-200 bg-brand-50">
              <p className="text-sm text-brand-700 mb-3">
                {t('articleLinkHomePrompt')}
              </p>
              <a
                href={homePaths[currentLang] || '/'}
                onClick={(e) => { e.preventDefault(); navigate(homePaths[currentLang] || '/'); }}
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> {t('backToHome')}
              </a>
            </div>

            {/* Related Articles */}
            <div className="mt-12">
              <h3 className="font-display text-lg font-bold text-warm-900 mb-4">{t('relatedTitle')}</h3>
              <div className="space-y-3">
                {relatedSlugs.map((s) => {
                  const rel = articles[s];
                  if (!rel) return null;
                  return (
                    <button
                      key={s}
                      type="button"
                      className="w-full flex items-center gap-3 p-4 rounded-xl border border-warm-200 bg-white hover:border-brand-300 hover:bg-brand-50 transition-all cursor-pointer text-left"
                      onClick={() => goToArticle(s)}
                    >
                      <ChevronRight className="w-4 h-4 text-warm-400 flex-shrink-0" />
                      <span className="text-sm text-warm-700">{rel.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </article>

        {/* Final CTA */}
        <section className="bg-warm-900 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-display text-xl md:text-2xl font-bold text-white mb-4">{t('finalCtaTitle')}</h2>
            <p className="text-warm-300 text-sm md:text-base mb-6 max-w-2xl mx-auto">{t('finalCtaDesc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-500 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
              >
                {t('finalCtaBtn1')} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors border border-white/20 whitespace-nowrap"
              >
                {t('finalCtaBtn2')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}